/**
 * lib/ninetailed/resolveIsMember.ts
 *
 * Derives the `isMember` signal sent to Ninetailed from the two auth signals the
 * app exposes, tolerating the async session-restore window.
 *
 * Why this exists — the returning-member flicker:
 *   `isLoggedIn` is derived purely from the in-memory user, which starts `null`
 *   on every mount and only becomes truthy AFTER the async `/api/auth/refresh`
 *   session restore resolves. So when a member returns to the app (e.g. back from
 *   an MFE) there is a brief window where `isLoggedIn` is `false` even though the
 *   user is actually a member. Sending `isMember: false` in that window makes
 *   Ninetailed resolve the non-member baseline, then flip to the member variant a
 *   moment later once refresh completes — a visible banner flicker.
 *
 *   `cachedInitials` is seeded SYNCHRONOUSLY from localStorage on the first render
 *   (written on login, cleared on logout), so its presence is a reliable early
 *   hint that this visitor was logged in last time. We use it to stay optimistic:
 *   treat the visitor as a member while `isLoggedIn` is still settling, and only
 *   send `false` when there is no cached hint either (a genuinely logged-out
 *   visitor). Once refresh resolves, `isLoggedIn` agrees and nothing changes; on
 *   an explicit logout the cache is cleared, so this correctly returns `false`.
 *
 * Trade-off: if a server-side session silently expired without an explicit logout
 * the cache can linger, so a returning visitor is optimistically shown the member
 * variant for one session. That is baseline-safe for personalization (no PII, and
 * the worst case is a member-styled banner) and matches the "avoid flicker" intent.
 */

export function resolveIsMember(
  isLoggedIn: boolean,
  cachedInitials: string,
): boolean {
  return isLoggedIn || cachedInitials.trim().length > 0;
}
